import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import { services } from '../../utils/constants';
import { FaArrowLeft, FaCheckCircle, FaLightbulb, FaCog } from 'react-icons/fa';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Call useScrollReveal hook at the top level
  useScrollReveal();
  
  // Find the service by ID
  const service = services.find(s => s.id === parseInt(id));
  
  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Show error if service not found
  if (!service) {
    return (
      <div className="service-not-found" style={{
        textAlign: 'center',
        padding: '4rem',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#333' }}>
          Service Not Found
        </h2>
        <p style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#666' }}>
          The requested service with ID "{id}" could not be found.
        </p>
        <p style={{ fontSize: '1rem', marginBottom: '2rem', color: '#888' }}>
          Available services: {services.map(s => s.id).join(', ')}
        </p>
        <button 
          onClick={() => navigate('/services')} 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'all 0.3s ease',
            textDecoration: 'none'
          }}
          onMouseOver={(e) => e.target.style.background = '#3730a3'}
          onMouseOut={(e) => e.target.style.background = '#4f46e5'}
        >
          <FaArrowLeft /> Back to Services
        </button>
      </div>
    );
  }

  return (
    <main className="service-detail" style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '2rem 1rem',
      color: '#333'
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate('/services')} 
        className="back-button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '2rem',
          padding: '0.75rem 1.5rem',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '1rem',
          transition: 'all 0.3s ease',
          color: '#475569'
        }}
        onMouseOver={(e) => {
          e.target.style.background = '#e2e8f0';
          e.target.style.transform = 'translateX(-2px)';
        }}
        onMouseOut={(e) => {
          e.target.style.background = '#f8fafc';
          e.target.style.transform = 'translateX(0)';
        }}
      >
        <FaArrowLeft /> Back to Services
      </button>

      {/* Hero Section */}
      <section className="service-hero" style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '3rem',
        marginBottom: '4rem'
      }}>
        <div className="service-hero__image" style={{
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          height: '400px'
        }}>
          <img 
            src={service.image} 
            alt={service.title} 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            loading="eager"
          />
        </div>
        
        <div className="service-hero__content" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)', 
            marginBottom: '1rem',
            fontWeight: '700',
            lineHeight: '1.2',
            color: '#1a202c'
          }}>
            {service.title}
          </h1>
          
          <p className="service-subtitle" style={{ 
            fontSize: '1.25rem', 
            color: '#4a5568', 
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            {service.description}
          </p>
          
          {/* Technologies */}
          <div className="service-tech" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '2rem'
          }}>
            {service.tags.map(tag => (
              <span 
                key={tag} 
                className="tech-tag"
                style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '25px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  border: 'none'
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Technologies List */}
          {service.technologies && (
            <div className="service-technologies" style={{
              padding: '1.5rem',
              background: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: '600',
                marginBottom: '1rem',
                color: '#2d3748'
              }}>
                Technologies We Use
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                {service.technologies.map(tech => (
                  <span 
                    key={tech}
                    style={{
                      padding: '0.25rem 0.75rem',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '15px',
                      fontSize: '0.875rem',
                      color: '#4a5568'
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Challenge Section */}
      <section className="service-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaLightbulb style={{ color: '#f59e0b' }} />
          Common Challenges
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          lineHeight: '1.8', 
          color: '#4a5568',
          marginBottom: '0'
        }}>
          {service.challenge}
        </p>
      </section>

      {/* Solution Section */}
      <section className="service-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaCog style={{ color: '#10b981' }} />
          Our Solution
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          lineHeight: '1.8', 
          color: '#4a5568',
          marginBottom: '0'
        }}>
          {service.solution}
        </p>
      </section>

      {/* Service Details Section */}
      <section className="service-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem'
        }}>
          What We Deliver
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <ul className="service-features" style={{
          listStyleType: 'none',
          paddingLeft: '0',
          display: 'grid',
          gap: '1rem'
        }}>
          {service.details.map((detail, index) => (
            <li 
              key={index} 
              style={{
                position: 'relative',
                paddingLeft: '2rem',
                fontSize: '1.125rem',
                lineHeight: '1.7',
                color: '#4a5568',
                display: 'flex',
                alignItems: 'flex-start'
              }}
            >
              <span style={{
                position: 'absolute',
                left: '0',
                top: '0.125rem',
                width: '20px',
                height: '20px',
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                ✓
              </span>
              {detail}
            </li>
          ))}
        </ul>
      </section>

      {/* Benefits Section */}
      <section className="service-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaCheckCircle style={{ color: '#10b981' }} />
          Benefits & Impact
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <div className="benefits-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {service.benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="benefit-card"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e2e8f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(79, 70, 229, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #10b981, #059669)'
              }}></div>
              <p style={{ 
                margin: '0', 
                lineHeight: '1.6',
                fontSize: '1.125rem',
                color: '#2d3748',
                fontWeight: '500'
              }}>
                {benefit}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      {service.process && (
        <section className="service-section" style={{ marginBottom: '4rem' }}>
          <h2 style={{ 
            fontSize: '1.875rem', 
            marginBottom: '1.5rem',
            fontWeight: '700',
            color: '#1a202c',
            position: 'relative',
            paddingBottom: '0.75rem'
          }}>
            Our Process
            <div style={{
              content: '""',
              position: 'absolute',
              bottom: '0',
              left: '0',
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
              borderRadius: '2px'
            }}></div>
          </h2>
          <div className="process-steps" style={{
            display: 'grid',
            gap: '1.5rem'
          }}>
            {service.process.map((step, index) => (
              <div 
                key={index}
                className="process-step"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1.5rem',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.125rem',
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>
                <p style={{
                  margin: '0',
                  fontSize: '1.125rem',
                  lineHeight: '1.6',
                  color: '#4a5568'
                }}>
                  {step}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="service-cta" style={{
        textAlign: 'center',
        padding: '3rem 2rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        color: 'white',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          marginBottom: '1rem',
          fontWeight: '600'
        }}>
          Ready to Get Started?
        </h3>
        <p style={{
          fontSize: '1.125rem',
          marginBottom: '2rem',
          opacity: '0.9'
        }}>
          Let's discuss how our {service.title.toLowerCase()} services can help your business grow.
        </p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => navigate('/contact')}
            style={{
              padding: '1rem 2rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Get In Touch
          </button>
          <button
            onClick={() => navigate('/work')}
            style={{
              padding: '1rem 2rem',
              background: 'transparent',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            View Our Work
          </button>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;